import React, { useEffect, useState } from "react";
import { Button, Typography, Dropdown } from "antd";
import {
  LeftOutlined,
  MoreOutlined,
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
} from "@ant-design/icons";
import styled from "styled-components";
import { ICourse, IQuizYear } from "utils/types";
import { useModalStore } from "store";
import ArticlePreview, { ArticlePreviewProps } from "./Preview";
import { useBreakpoint } from "hooks/media/useBreakpoint";

const { Text } = Typography;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 20px 40px;
  font-family: "Manrope", sans-serif;

  @media (max-width: 768px) {
    padding: 15px;
  }
`;

const NavHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 24px;
`;

const Body = styled.div`
  display: flex;
  gap: 20px;
  justify-content: space-between;
`

const BackButton = styled(Button)`
  display: flex;
  align-items: center;
  color: #5575f7;
  font-size: 14px;
  padding: 0;
  margin-right: 8px;
  border: none;
  box-shadow: none;

  &:hover {
    color: #4563e6;
    background: none;
  }
`;

const CourseContainer = styled.div`
  margin-bottom: 40px;
`;

const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  width: 100%;
  margin-bottom: 16px;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const CourseInfoContainer = styled.div`
  display: flex;
  gap: 20px;

  @media (max-width: 576px) {
    flex-direction: column;
    align-items: center;
    text-align: center;
  }
`;

const CourseImage = styled.div`
  width: 70px;
  height: 70px;
  border-radius: 50%;
  background-size: cover;
  background-position: center;
  overflow: hidden;
  flex-shrink: 0;
`;

const CourseDetails = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 700px;
  gap: 10px;

  @media (max-width: 576px) {
    align-items: center;
  }
`;
const CourseTitleContainer = styled.div`
  display: flex;
  gap: 20px;
  justify-content: center;
  align-items: center;
`
const CourseTitle = styled.h1`
  font-size: 22px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0px;
  margin: 0px;

  @media (max-width: 576px) {
    flex-direction: column;
    text-align: center;
    gap: 8px;
  }
`;

const CardBadge = styled.div<{ $isPremium: boolean }>`
  top: 12px;
  right: 12px;
  padding: 4px 8px;
  font-size: 12px;
  font-weight: 500;
  border-radius: 4px;
  color: white;
  background-color: ${(props) => (props.$isPremium ? "#FFB800" : "#5575F7")};
`;

const CourseDescription = styled.p`
  font-size: 14px;
  line-height: 1.5;
  color: #666;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 13px;
  }
`;

const MoreButton = styled(Button)`
  background: transparent;
  border: none;
  box-shadow: none;
  padding: 0;
  height: auto;

  @media (max-width: 768px) {
    align-self: flex-end;
    margin-top: 8px;
  }
`;

const QuestionsSection = styled.div`
  margin-top: 30px;
`;

const QuestionsLabel = styled.div`
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 16px;
`;

const QuestionsContainer = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
`;

const QuestionCard = styled.div`
  width: 180px;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  overflow: hidden;
  font-family: "Manrope", sans-serif;
  transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out;

  &:hover {
    transform: translateY(-4px);
  }

  @media (max-width: 480px) {
    width: 100%;
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
`;

const YearText = styled.span`
  font-weight: 500;
  font-size: 16px;
`;

const CardContent = styled.div`
  padding: 12px 16px;
`;

const QuestionsCount = styled.span`
  font-size: 16px;
  font-weight: 500;
  font-family: "Manrope", sans-serif;
`;

const AddCard = styled.div`
  width: 180px;
  height: 112px;
  border: 1px dashed #d9d9d9;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  background: #f2faff;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s;
  font-family: "Manrope", sans-serif;

  &:hover {
    border-color: #5575f7;
  }

  @media (max-width: 480px) {
    width: 100%;
  }
`;

const AddIcon = styled(PlusOutlined)`
  font-size: 20px;
  color: #5575f7;
  margin-bottom: 8px;
`;

export interface CourseProps {
  articlePreviewProps: Omit<ArticlePreviewProps, "onGetCourse"|"onBack"|"onMoveToCourseStudy">;
  onBack: () => void;
  onAddQuestion: () => void;
  course?: ICourse;
  courseId: string;
  onGetCourse: (courseId: string) => Promise<ICourse|undefined>;
  onDeleteQuiz: (quizId: string) => Promise<IQuizYear[]>;
  onDeleteCourse: (courseId: string) => Promise<boolean>;
  onGetQuizYears: (courseId: string) => Promise<IQuizYear[]>;
  onMoveToEditQuiz: (quizId: string) => void;
  onMoveToEditCourse: (course: ICourse) => void;
}
export const Course: React.FC<CourseProps> = ({
  onBack, onAddQuestion, course: initCourse, courseId,
  onGetCourse, onDeleteQuiz, onDeleteCourse, onGetQuizYears,
  onMoveToEditCourse, onMoveToEditQuiz, articlePreviewProps
}) => {
  const [course, setCourse] = useState<ICourse | undefined>(initCourse);
  const [quizYears, setQuizYears] = useState<IQuizYear[]>([]);

  const { openModal, closeModal } = useModalStore();
  const { up } = useBreakpoint();

  useEffect(() => {
    if(courseId){
      onGetQuizYears(courseId).then(setQuizYears);
    }
  }, [courseId])
  useEffect(() => {
      onGetCourse(courseId).then((course) => {
        if(!course){
          onBack();
        }
        setCourse(course);
      })
  }, [courseId])

  const courseMenuItems = [
    {
      key: "edit",
      label: "Edit details",
      icon: <EditOutlined />,
      onClick: () => {
        if(!course) return;
        onMoveToEditCourse(course)
      },
    },
    {
      key: "delete",
      label: "Delete",
      icon: <DeleteOutlined />,
      danger: true,
      onClick: () => {
        openModal(
          "Are you sure you want to delete this course?",
          "Once deleted, it will be permanently removed and cannot be recovered. This action is irreversible.",
          async () => {
            if(!course) return;
            await onDeleteCourse(course.id);
            onBack();
          },
          true
        )
      },
    },
  ];

  const getQuizMenuItems = (quizYear: IQuizYear) => [
    {
      key: "edit",
      label: "Edit questions",
      icon: <EditOutlined />,
      onClick: () => {
        onMoveToEditQuiz(quizYear.quizId);
      },
    },
    {
      key: "delete",
      label: "Delete",
      icon: <DeleteOutlined />,
      danger: true,
      onClick: () => {
        openModal(
          "Are you sure you want to delete past questions?",
          "Once deleted, it will be permanently removed and cannot be recovered. This action is irreversible.",
          async () => {
            const quizYears = await onDeleteQuiz(quizYear.quizId);
            setQuizYears(quizYears);
            closeModal();
          },
          true
        )
      },      
    },
  ];
  return (
    <Container>
      <NavHeader>
        <BackButton type="text" icon={<LeftOutlined />} onClick={onBack}>
          Courses management
        </BackButton>
      </NavHeader>
      <Body>
      <CourseContainer>
        <HeaderSection>
          <CourseInfoContainer>
            <CourseImage
              style={{
                backgroundImage: `url(${course?.image || "/usmle-image.jpg"})`,
                backgroundColor: "#e0e7ff",
              }}
            />
            <CourseDetails>
              <CourseTitleContainer>
                <CourseTitle>
                  {course?.title}
                </CourseTitle>
                <CardBadge {...{ $isPremium: course?.courseType === "premium"}} >{course?.courseType}</CardBadge >
                <Dropdown                  
                  menu={{ items: courseMenuItems }}
                  trigger={["click"]}
                  placement="bottomRight"
                >
                  <MoreButton
                    style={{marginLeft: 10}}
                    icon={<MoreOutlined style={{ fontSize: "20px" }} />}
                    type="text"
                    onClick={(e) => e.preventDefault()}
                  />
                </Dropdown>
              </CourseTitleContainer>
              <CourseDescription>
                {course?.description}
              </CourseDescription>
            </CourseDetails>
          </CourseInfoContainer>
        </HeaderSection>

        <QuestionsSection>
          <QuestionsLabel>Available past questions:</QuestionsLabel>
          <QuestionsContainer>
            {quizYears?.map((quiz) => {
              return (
                <QuestionCard key={quiz.quizId}>
                  <CardHeader>
                    <YearText>{quiz.quizYear}</YearText>
                    <Dropdown
                      menu={{
                        items: getQuizMenuItems(quiz),
                      }}
                      trigger={["click"]}
                      placement="bottomRight"
                    >
                      <MoreOutlined
                        style={{ cursor: "pointer" }}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </Dropdown>
                  </CardHeader>
                  <CardContent>
                    <QuestionsCount>{quiz.totalQuestions}</QuestionsCount> <Text>questions</Text>
                  </CardContent>
                </QuestionCard>
              )
            })}
            
            <AddCard onClick={onAddQuestion}>
              <AddIcon />
              <Text style={{ color: "#5575f7", fontFamily: "Manrope" }}>
                Add past questions
              </Text>
            </AddCard>
          </QuestionsContainer>
        </QuestionsSection>
      </CourseContainer>
      {up("lg") && <ArticlePreview {...{ 
        internal: true,
        course, 
        onGetCourse(course) {
          return onGetCourse(course.id as string)
        },
        onBack() {
          
        },
        ...articlePreviewProps }} />}
      </Body>
    </Container>
  );
};
